import { Anchor, H5 } from "@sellar-io/components";
import { useMemo, useState } from "react";
import type {
  CheckoutSupplierPaymentOptionsResponseType,
  OrderPaymentType,
} from "@sellar-io/api-v2/types";
import { Icon, SpinnerLegacy } from "../../../../components";
import {
  RadioGroup,
  RadioGroupOption,
  RadioGroupContainer,
} from "../../../../components/radioSelector/RadioGroup";
import { Alert } from "../../../../components/alert/Alert";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
} from "../../../../components/modal/Dialog";
import { Switch } from "@sellar-io/components";
import { useRemoteFeatureFlags } from "../../../../hooks/useRemoteFeatureFlags";

interface SupplierPaymentTypeRadioGroupProps {
  paymentTypes: CheckoutSupplierPaymentOptionsResponseType["paymentTypes"];
  isLoading: boolean;
  paymentType: OrderPaymentType | undefined;
  onChange: (value: OrderPaymentType) => void;
  paymentTypeAlertActive: boolean;
  paymentLinkRequested: boolean;
  onChangePaymentLinkRequested: (checked: boolean) => void;
}

export const SupplierPaymentTypeRadioGroup = ({
  paymentTypes,
  isLoading,
  paymentType,
  onChange,
  paymentTypeAlertActive,
  paymentLinkRequested,
  onChangePaymentLinkRequested,
}: SupplierPaymentTypeRadioGroupProps) => {
  const { isActive } = useRemoteFeatureFlags();
  const isCheckoutPaymentLinkRequestActive = isActive(
    "checkoutPaymentLinkRequest",
  );
  const [infoModalOpen, setInfoModalOpen] = useState(false);

  const stripeAvailable = paymentTypes.includes("stripe");
  const manualAvailable = paymentTypes.includes("manual");
  const sellarCreditAvailable = paymentTypes.includes("sellarCredit");

  const availableOptions = useMemo(
    () =>
      isCheckoutPaymentLinkRequestActive
        ? [
            {
              value: "sellarCredit",
              isAvailable: sellarCreditAvailable,
              label: (
                <H5 className="inline-flex gap-2">
                  Use{" "}
                  <span className="flex items-center gap-1 bg-linear-to-br from-[#B557F1] to-[#D213A6] bg-clip-text font-semibold text-nowrap text-transparent">
                    <Icon
                      icon="SELLAR_NEW"
                      size={0.6669}
                      color="#D213A6"
                      className="shrink-0"
                    />
                    Sellar Credit
                  </span>
                </H5>
              ),
            },
            {
              value: "manual",
              isAvailable: manualAvailable || stripeAvailable,
              label: (
                <div className="flex flex-col">
                  <H5>Direct customer payment</H5>
                </div>
              ),
            },
          ].filter((option) => option.isAvailable)
        : [
            {
              value: "sellarCredit",
              isAvailable: sellarCreditAvailable,
              label: (
                <H5 className="inline-flex gap-2">
                  Use{" "}
                  <span className="flex items-center gap-1 bg-linear-to-br from-[#B557F1] to-[#D213A6] bg-clip-text font-semibold text-nowrap text-transparent">
                    <Icon
                      icon="SELLAR_NEW"
                      size={0.6669}
                      color="#D213A6"
                      className="shrink-0"
                    />
                    Sellar Credit
                  </span>
                </H5>
              ),
            },
            {
              value: "stripe",
              isAvailable: stripeAvailable,
              label: (
                <div className="flex flex-col">
                  <H5>Card payment link</H5>
                </div>
              ),
            },
            {
              value: "manual",
              isAvailable: manualAvailable,
              label: (
                <div className="flex flex-col">
                  <H5>BACS Manual invoice</H5>
                </div>
              ),
            },
          ].filter((option) => option.isAvailable),
    [
      isCheckoutPaymentLinkRequestActive,
      manualAvailable,
      sellarCreditAvailable,
      stripeAvailable,
    ],
  );

  if (isLoading) {
    return (
      <div className="flex justify-center p-4">
        <SpinnerLegacy />
      </div>
    );
  }

  const showPaymentLinkToggle =
    isCheckoutPaymentLinkRequestActive &&
    paymentType === "manual" &&
    stripeAvailable;

  return (
    <>
      <RadioGroup
        value={paymentType ?? ""}
        onChange={(value) => onChange(value as OrderPaymentType)}
      >
        <RadioGroupContainer>
          {availableOptions.length === 0 ? (
            <p className="text-zinc-500">
              No applicable payment options available
            </p>
          ) : (
            availableOptions.map(({ value, label }) => (
              <RadioGroupOption key={value} value={value}>
                {label}
              </RadioGroupOption>
            ))
          )}
        </RadioGroupContainer>
      </RadioGroup>

      {showPaymentLinkToggle && (
        <div className="mt-2 flex items-center gap-4 rounded-lg bg-zinc-100 p-4">
          <Switch
            checked={paymentLinkRequested}
            onChange={onChangePaymentLinkRequested}
          />
          <div className="flex flex-1 flex-col">
            <div className="flex items-center gap-2">
              <span className="text-base leading-6 font-semibold text-zinc-800">
                Send a card payment link
              </span>
            </div>
            <p className="text-base leading-6 font-normal text-zinc-600">
              A link to pay by card will be sent to your customer's email
              address when payment for this order is due.{" "}
              <span
                onClick={() => setInfoModalOpen(true)}
                className={
                  "inline-flex cursor-pointer items-center gap-1 align-middle text-blue-700"
                }
              >
                Learn more
                <Icon icon={"ARROW_TOP_RIGHT"} />
              </span>
            </p>
          </div>
        </div>
      )}

      {paymentTypeAlertActive && (
        <Alert
          message={"Please select a payment method before continuing"}
          type={"error"}
          className={"mt-2 w-full"}
        />
      )}

      <div className="mt-2 flex gap-1 text-zinc-500 md:items-center">
        <Icon icon={"INFORMATION_OUTLINE"} className={"flex-none"} />
        {sellarCreditAvailable && paymentType === "sellarCredit" ? (
          <p>
            Customers who pay with Sellar Credit can add this order to their
            tab.{" "}
            <Anchor
              target="_blank"
              className="text-zinc-800 underline"
              rel="noopener noreferrer"
              href="https://intercom.help/sellario/en/articles/11755960-supplier-basics-raising-orders-for-your-customers#h_b86912d39a"
            >
              Learn More
            </Anchor>
          </p>
        ) : (
          <p>Select a payment type from the options above</p>
        )}
      </div>

      <Dialog active={infoModalOpen} onDismiss={() => setInfoModalOpen(false)}>
        <DialogNavigation
          onClickLeftIcon={() => setInfoModalOpen(false)}
          leftIcon={"CHEVRON_DOWN"}
          title={"About card payment links"}
        />
        <DialogContent className={"px-4 pb-4"}>
          <div className="flex flex-col gap-3 text-base text-zinc-700">
            <p>
              By toggling on{" "}
              <span className="font-semibold">
                &lsquo;Send a card payment link&rsquo;
              </span>
              , your customer will be sent a link to their email address when
              payment for their order is due.
            </p>
            <ul className="list-disc pl-5">
              <li>
                Your customer can access the link any time from their view of
                the order on Sellar.
              </li>
              <li>
                Payment is made directly to your connected Stripe account.
              </li>
            </ul>
            <Alert
              type={"information"}
              title={"You are still responsible for invoicing the customer"}
              message={
                <>
                  The payment link simply allows you to collect payment through
                  Sellar.{" "}
                  <span className="font-semibold">
                    If you don&rsquo;t include a card payment link
                  </span>
                  , you will be responsible for collecting payment from your
                  customer outside of Sellar.
                </>
              }
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
